import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClipboardPasteIcon, CameraIcon, DownloadIcon, MaximizeIcon, MoveLeftIcon, MoveRightIcon, Trash2Icon, XIcon } from 'lucide-react';
import { OperationImage } from '../../lib/types';
import { uid } from '../../lib/id';
import { cn } from '../../lib/cn';
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
function pastedName(index: number): string {
  return `Captura ${index}`;
}

/** Reads a File/Blob into a data URL so it can be stored locally. */
function fileToImage(file: File | Blob, fallbackName: string): Promise<OperationImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      id: uid('img'),
      url: String(reader.result),
      name: file instanceof File && file.name ? file.name : fallbackName
    });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
export function ImageUploader({
  images,
  onChange
}: {
  images: OperationImage[];
  onChange: (imgs: OperationImage[]) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<OperationImage | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const ingest = useCallback(async (files: Array<File | Blob>) => {
    const list = files.filter(file => file.type.startsWith('image/'));
    if (!list.length) return;
    const start = imagesRef.current.length + 1;
    const added = await Promise.all(list.map((file, index) => fileToImage(file, pastedName(start + index))));
    onChange([...imagesRef.current, ...added]);
    setNotice(added.length === 1 ? 'Captura añadida' : `${added.length} capturas añadidas`);
  }, [onChange]);

  // Notion-like behaviour: Ctrl/Cmd + V works anywhere inside the form,
  // without focusing the dropzone first.
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      const files = Array.from(items).filter(item => item.kind === 'file' && item.type.startsWith('image/')).map(item => item.getAsFile()).filter((file): file is File => !!file);
      if (!files.length) return;
      event.preventDefault();
      void ingest(files);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [ingest]);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 1600);
    return () => window.clearTimeout(timer);
  }, [notice]);
  const readFromClipboardApi = async () => {
    try {
      const contents = await navigator.clipboard.read();
      const blobs: Blob[] = [];
      for (const item of contents) {
        const type = item.types.find(candidate => candidate.startsWith('image/'));
        if (type) blobs.push(await item.getType(type));
      }
      if (blobs.length) {
        await ingest(blobs);
      } else {
        setNotice('No hay ninguna imagen en el portapapeles');
      }
    } catch {
      setNotice('Usa Ctrl+V / Cmd+V para pegar la captura');
    }
  };
  const move = (id: string, direction: -1 | 1) => {
    const next = [...images];
    const index = next.findIndex(image => image.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  const download = (image: OperationImage) => {
    const link = document.createElement('a');
    link.href = image.url;
    link.download = `${image.name || 'captura'}.png`;
    link.click();
  };
  return <div className="flex flex-col gap-3" data-id="element-389" data-mp="52h4b4zbzkay">
      <div role="button" tabIndex={0} onClick={() => inputRef.current?.click()} onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        inputRef.current?.click();
      }
    }} onDragOver={event => {
      event.preventDefault();
      setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={event => {
      event.preventDefault();
      setDragging(false);
      void ingest(Array.from(event.dataTransfer.files));
    }} className={cn('flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-7 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-accent/20', dragging ? 'border-accent bg-accent-soft' : 'border-line bg-surface/50 hover:border-accent/40 hover:bg-surface')} data-id="element-390" data-mp="a00ftx4n9iz7">
        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-white" data-id="element-391" data-mp="2eyyvzdd1of6y">
          <CameraIcon className="h-4 w-4 text-ink-soft" data-id="element-392" data-mp="29yklzprvuv6a" />
        </div>
        <p className="text-[13px] font-medium text-ink" data-id="element-393" data-mp="2hjepx8mpect7">Pega una imagen (Ctrl+V / Cmd+V)</p>
        <p className="text-[12px] text-ink-soft" data-id="element-394" data-mp="2mi7ugbk0ith8">o arrástrala aquí · o selecciona archivos</p>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-1.5" data-id="element-395" data-mp="2rb0y0w3qvvrq">
          <span className="inline-flex h-8 items-center rounded-lg border border-line bg-white px-3 text-[12px] font-medium text-ink" data-id="element-396" data-mp="2w9lxuuf34dfn">Seleccionar archivos</span>
          <button type="button" onClick={event => {
          event.stopPropagation();
          void readFromClipboardApi();
        }} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-[12px] font-medium text-ink-soft hover:text-ink" data-id="element-397" data-mp="1ynexxxtq799m">
            <ClipboardPasteIcon className="h-3.5 w-3.5" data-id="element-398" data-mp="2dg8bvetcnhc6" />Pegar del portapapeles
          </button>
        </div>
        <p className="text-[11px] text-ink-soft" data-id="element-399" data-mp="3oxq5hlh7v0kz">PNG · JPG · WEBP · varias capturas por operación</p>
        <input ref={inputRef} type="file" accept={ACCEPTED.join(',')} multiple hidden onChange={event => {
        if (event.target.files) void ingest(Array.from(event.target.files));
        event.target.value = '';
      }} data-id="element-400" data-mp="1ovi9dvr123xl" />
      </div>

      {notice && <p className="text-[12px] font-medium text-accent" data-id="element-401" data-mp="1tubm3lyhpqwp">{notice}</p>}

      {images.length > 0 && <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-402" data-mp="1662999j98dhp">
          {images.map((image, index) => <li key={image.id} className="overflow-hidden rounded-xl border border-line bg-white" data-id="element-403" data-mp="1177f5bfu72ia" data-mpi={index}>
              <button type="button" onClick={() => setPreview(image)} className="block aspect-video w-full overflow-hidden bg-surface" aria-label={`Ampliar ${image.name}`} data-id="element-404" data-mp="1ey46z01xvfoa">
                <img src={image.url} alt={image.name} className="h-full w-full object-cover" data-id="element-405" data-mp="ifeldesy1d9i" />
              </button>
              <div className="flex items-center justify-between gap-1 px-2 py-1.5" data-id="element-406" data-mp="8ay5wmjdly68">
                <span className="min-w-0 flex-1 truncate text-[11px] text-ink-soft" data-id="element-407" data-mp="d1y3lu0bkk9s">{image.name}</span>
                <div className="flex items-center gap-0.5" data-id="element-408" data-mp="3uiy62xg2jyrh">
                  <IconAction label="Mover a la izquierda" disabled={index === 0} onClick={() => move(image.id, -1)} data-id="element-409" data-mp="3ukiyohitp7mp"><MoveLeftIcon className="h-3.5 w-3.5" data-id="element-410" data-mp="3coacatqovk3" /></IconAction>
                  <IconAction label="Mover a la derecha" disabled={index === images.length - 1} onClick={() => move(image.id, 1)} data-id="element-411" data-mp="r30tkhufqepz"><MoveRightIcon className="h-3.5 w-3.5" data-id="element-412" data-mp="vzr4kqrduk4j" /></IconAction>
                  <IconAction label="Ampliar" onClick={() => setPreview(image)} data-id="element-413" data-mp="h4l0wafrexed"><MaximizeIcon className="h-3.5 w-3.5" data-id="element-414" data-mp="h65thuiik69l" /></IconAction>
                  <IconAction label="Descargar" onClick={() => download(image)} data-id="element-415" data-mp="m1bfsuftvmy7"><DownloadIcon className="h-3.5 w-3.5" data-id="element-416" data-mp="31tiqdudglhp8" /></IconAction>
                  <IconAction label="Eliminar" tone="neg" onClick={() => onChange(images.filter(item => item.id !== image.id))} data-id="element-417" data-mp="36t3sueru886p"><Trash2Icon className="h-3.5 w-3.5" data-id="element-418" data-mp="2o2b337lqw1ku" /></IconAction>
                </div>
              </div>
            </li>)}
        </ul>}

      {preview && <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/85 p-6" onClick={() => setPreview(null)} data-id="element-419" data-mp="2j5ufqfmddr8t">
          <button type="button" className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20" aria-label="Cerrar vista ampliada" data-id="element-420" data-mp="2a3be1yublko5"><XIcon className="h-5 w-5" data-id="element-421" data-mp="31tzawyoyo3w0" /></button>
          <img src={preview.url} alt={preview.name} className="max-h-full max-w-full rounded-xl object-contain" onClick={event => event.stopPropagation()} data-id="element-422" data-mp="31vaeoyiepbn4" />
        </div>}
    </div>;
}
function IconAction({
  label,
  onClick,
  disabled,
  tone = 'neutral',
  children
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'neutral' | 'neg';
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} className={cn('flex h-6 w-6 items-center justify-center rounded-md text-ink-soft transition-colors disabled:opacity-25', tone === 'neg' ? 'hover:bg-neg-soft hover:text-neg' : 'hover:bg-surface hover:text-ink')} data-id="element-423" data-mp="3krk2wen0viku">
      {children}
    </button>;
}